import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/Navigation';

export default function HomePage() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [newsletterMessage, setNewsletterMessage] = useState('');

  const heroImages = [
    "https://public.readdy.ai/ai/img_res/5e62d09d2ba43ed466da1204ead32b28.jpg",
    "https://readdy.ai/api/search-image?query=close%20up%20of%20priest%20hands%20lighting%20traditional%20brass%20diya%20lamp%20during%20Hindu%20ceremony%2C%20sacred%20fire%20flames%2C%20flower%20petals%20scattered%20around%2C%20sandalwood%20paste%2C%20kumkum%20powder%2C%20spiritual%20ritual%20items%2C%20warm%20amber%20temple%20lighting%2C%20devotional%20atmosphere%2C%20authentic%20traditional%20setting%2C%20soft%20focus%20background&width=800&height=600&seq=hero002&orientation=landscape",
    "https://readdy.ai/api/search-image?query=beautiful%20arrangement%20of%20Hindu%20puja%20thali%20with%20multiple%20diyas%2C%20fresh%20jasmine%20and%20marigold%20garlands%2C%20coconut%2C%20betel%20leaves%2C%20sacred%20red%20thread%2C%20brass%20vessels%2C%20incense%20sticks%20with%20smoke%2C%20warm%20golden%20lighting%2C%20traditional%20spiritual%20ceremony%20setup%2C%20overhead%20composition%2C%20authentic%20devotional%20elements&width=800&height=600&seq=hero003&orientation=landscape",
    "https://readdy.ai/api/search-image?query=traditional%20Hindu%20temple%20interior%20with%20ornate%20brass%20lamps%2C%20flower%20decorations%2C%20devotees%20performing%20aarti%20ceremony%20warm%20golden%20lighting%20from%20oil%20lamps%20sacred%20atmosphere%20architectural%20details%20spiritual%20gathering%20authentic%20religious%20setting%20soft%20ambient%20glow%20devotional%20mood&width=800&height=600&seq=hero004&orientation=landscape"
  ];

  // ------- Image carousel -------
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // ------- Newsletter handling -------
  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newsletterEmail || !newsletterEmail.includes('@')) {
      setNewsletterStatus('error');
      setNewsletterMessage('Please enter a valid email address');
      return;
    }

    setNewsletterStatus('submitting');

    try {
      const formData = new URLSearchParams();
      formData.append('email', newsletterEmail);

      const response = await fetch('https://readdy.ai/api/form/d5st2e8t9f9av9ls7ng0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (!response.ok) {
        throw new Error('Subscription failed');
      }

      setNewsletterStatus('success');
      setNewsletterMessage('Thank you for subscribing!');
      setNewsletterEmail('');
    } catch (error) {
      console.error('Newsletter error:', error);
      setNewsletterStatus('error');
      setNewsletterMessage('Something went wrong. Please try again.');
    } finally {
      // Reset status/message after a short delay so UI feedback is visible
      setTimeout(() => {
        setNewsletterStatus('idle');
        setNewsletterMessage('');
      }, 5000);
    }
  };

  // ------- Chat widget trigger -------
  const handleChatWithUs = () => {
    const vapiButton = document.querySelector('#vapi-widget-floating-button') as HTMLElement | null;
    if (vapiButton) {
      vapiButton.click();
    } else {
      console.warn('Chat widget button not found');
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <Navigation />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          {heroImages.map((img, idx) => (
            <div
              key={idx}
              className="absolute inset-0 transition-opacity duration-1000"
              style={{ opacity: currentImageIndex === idx ? 1 : 0 }}
            >
              <img src={img} alt={`Traditional Puja Ritual ${idx + 1}`} className="w-full h-full object-cover object-top" />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6">
              <i className="ri-ancient-gate-line text-[#C78A2B]"></i>
              <span className="text-sm text-white/90">Authentic Vedic Rituals Since 2020</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight mb-6">
              Sacred Rituals,
              <br />
              <span className="font-semibold text-[#C78A2B]">Performed with Devotion</span>
            </h1>

            <p className="text-lg sm:text-xl text-white/80 leading-relaxed mb-8 font-light">
              Connect with your spiritual roots from anywhere in the world. Authentic Hindu rituals performed by verified priests at
              sacred temples across India.
            </p>

            <div className="flex flex-wrap items-center gap-6 mb-10 text-sm text-white/70">
              <span className="flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-[#C78A2B]"></i>500+ Rituals
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-[#C78A2B]"></i>50+ Verified Priests
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-[#C78A2B]"></i>30+ Countries
              </span>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <Link
                to="/book-ritual"
                className="px-8 py-4 bg-[#C78A2B] text-white rounded-xl text-base font-medium hover:bg-[#B8860B] transition-all hover:shadow-lg whitespace-nowrap"
              >
                Book a Ritual
              </Link>
              <Link
                to="/rituals"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/30 text-white rounded-xl text-base font-medium hover:bg-white/20 transition-all whitespace-nowrap"
              >
                Explore Services
              </Link>
            </div>
          </div>
        </div>

        {/* Image indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex items-center gap-2 z-10">
          {heroImages.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentImageIndex(idx)}
              className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                currentImageIndex === idx ? 'bg-[#C78A2B] w-8' : 'bg-white/50'
              }`}
              aria-label={`Go to image ${idx + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-light text-[#2E2E2E] mb-4">Our Services</h2>
            <div className="w-24 h-0.5 bg-[#C78A2B] mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link to="/rituals" className="bg-[#FAF7F2] rounded-2xl p-6 hover:shadow-xl transition-all group">
              <div className="w-14 h-14 rounded-full bg-[#C78A2B] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-ancient-gate-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-[#2E2E2E] mb-2">Ritual Services</h3>
              <p className="text-sm text-gray-600 font-light mb-3">Life events, Shradh, daily pujas</p>
              <span className="text-[#C78A2B] text-sm font-medium">Starting ₹1,500 →</span>
            </Link>

            <Link to="/festivals" className="bg-[#FAF7F2] rounded-2xl p-6 hover:shadow-xl transition-all group">
              <div className="w-14 h-14 rounded-full bg-[#C78A2B] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-calendar-event-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-[#2E2E2E] mb-2">Festival Pujas</h3>
              <p className="text-sm text-gray-600 font-light mb-3">Diwali, Navratri, Ganesh Chaturthi</p>
              <span className="text-[#C78A2B] text-sm font-medium">View Calendar →</span>
            </Link>

            <Link to="/festival-potlis" className="bg-[#FAF7F2] rounded-2xl p-6 hover:shadow-xl transition-all group">
              <div className="w-14 h-14 rounded-full bg-[#C78A2B] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-gift-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-[#2E2E2E] mb-2">Festival Potlis</h3>
              <p className="text-sm text-gray-600 font-light mb-3">Puja samagri delivered home</p>
              <span className="text-[#C78A2B] text-sm font-medium">Starting ₹999 →</span>
            </Link>

            <Link to="/temples" className="bg-[#FAF7F2] rounded-2xl p-6 hover:shadow-xl transition-all group">
              <div className="w-14 h-14 rounded-full bg-[#C78A2B] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-home-heart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-[#2E2E2E] mb-2">Temple Pooja</h3>
              <p className="text-sm text-gray-600 font-light mb-3">Kashi, Tirupati, Kedarnath</p>
              <span className="text-[#C78A2B] text-sm font-medium">Explore Temples →</span>
            </Link>

            <Link to="/corporate-ceremonies" className="bg-[#FAF7F2] rounded-2xl p-6 hover:shadow-xl transition-all group">
              <div className="w-14 h-14 rounded-full bg-[#C78A2B] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-building-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-[#2E2E2E] mb-2">Corporate Ceremonies</h3>
              <p className="text-sm text-gray-600 font-light mb-3">Office pujas & inaugurations</p>
              <span className="text-[#C78A2B] text-sm font-medium">Starting ₹5,000 →</span>
            </Link>

            <Link to="/donations" className="bg-[#FAF7F2] rounded-2xl p-6 hover:shadow-xl transition-all group">
              <div className="w-14 h-14 rounded-full bg-[#C78A2B] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-hand-heart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-[#2E2E2E] mb-2">Donations & Seva</h3>
              <p className="text-sm text-gray-600 font-light mb-3">Temple support, Annadan</p>
              <span className="text-[#C78A2B] text-sm font-medium">Contribute →</span>
            </Link>

            <Link to="/spiritual-merchandise" className="bg-[#FAF7F2] rounded-2xl p-6 hover:shadow-xl transition-all group">
              <div className="w-14 h-14 rounded-full bg-[#C78A2B] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-store-2-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-[#2E2E2E] mb-2">Spiritual Merchandise</h3>
              <p className="text-sm text-gray-600 font-light mb-3">Puja items, idols, malas</p>
              <span className="text-[#C78A2B] text-sm font-medium">Shop Now →</span>
            </Link>

            <Link to="/online-classes" className="bg-[#FAF7F2] rounded-2xl p-6 hover:shadow-xl transition-all group">
              <div className="w-14 h-14 rounded-full bg-[#C78A2B] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-graduation-cap-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-[#2E2E2E] mb-2">Online Classes</h3>
              <p className="text-sm text-gray-600 font-light mb-3">Vedic learning & mantras</p>
              <span className="text-[#C78A2B] text-sm font-medium">Learn More →</span>
            </Link>

            <Link to="/senior-citizen-services" className="bg-[#FAF7F2] rounded-2xl p-6 hover:shadow-xl transition-all group">
              <div className="w-14 h-14 rounded-full bg-[#C78A2B] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-heart-2-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-[#2E2E2E] mb-2">Senior Citizen Services</h3>
              <p className="text-sm text-gray-600 font-light mb-3">Compassionate spiritual care</p>
              <span className="text-[#C78A2B] text-sm font-medium">Explore →</span>
            </Link>
          </div>

          <div className="text-center mt-10">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-8 py-3 bg-[#C78A2B] text-white rounded-xl text-base font-medium hover:bg-[#B8860B] transition-all whitespace-nowrap"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-16 sm:py-20 px-4 sm:px-6 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-light text-[#2E2E2E] mb-4">How It Works</h2>
            <div className="w-24 h-0.5 bg-[#C78A2B] mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-14 h-14 rounded-full border-2 border-[#C78A2B] flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-light text-[#2E2E2E]">1</span>
              </div>
              <h3 className="text-lg font-medium text-[#2E2E2E] mb-2">Choose a Ritual</h3>
              <p className="text-sm text-gray-600 font-light">Select the ritual that aligns with your need</p>
            </div>

            <div className="text-center">
              <div className="w-14 h-14 rounded-full border-2 border-[#C78A2B] flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-light text-[#2E2E2E]">2</span>
              </div>
              <h3 className="text-lg font-medium text-[#2E2E2E] mb-2">Share Details</h3>
              <p className="text-sm text-gray-600 font-light">Provide names, gotra, and preferred date</p>
            </div>

            <div className="text-center">
              <div className="w-14 h-14 rounded-full border-2 border-[#C78A2B] flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-light text-[#2E2E2E]">3</span>
              </div>
              <h3 className="text-lg font-medium text-[#2E2E2E] mb-2">Ritual Performed</h3>
              <p className="text-sm text-gray-600 font-light">Priest performs at temple or sacred location</p>
            </div>

            <div className="text-center">
              <div className="w-14 h-14 rounded-full border-2 border-[#C78A2B] flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-light text-[#2E2E2E]">4</span>
              </div>
              <h3 className="text-lg font-medium text-[#2E2E2E] mb-2">Get Confirmation</h3>
              <p className="text-sm text-gray-600 font-light">Photos, videos, and updates shared digitally</p>
            </div>
          </div>

          <div className="text-center mt-10">
            <Link
              to="/book-ritual"
              className="inline-flex items-center gap-2 px-8 py-3 bg-[#C78A2B] text-white rounded-xl text-base font-medium hover:bg-[#B8860B] transition-all whitespace-nowrap"
            >
              Book a Ritual
            </Link>
          </div>
        </div>
      </section>

      {/* For NRIs */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
            <div className="w-full lg:w-[45%]">
              <div className="rounded-2xl overflow-hidden shadow-xl" style={{ height: '400px' }}>
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20NRI%20family%20in%20contemporary%20Western%20home%20performing%20traditional%20Hindu%20puja%20together%2C%20natural%20window%20light%2C%20parents%20and%20children%20in%20casual%20Western%20clothing%20with%20traditional%20elements%2C%20diya%20lamps%20and%20flowers%20on%20table%2C%20warm%20family%20atmosphere%2C%20desaturated%20sophisticated%20tones%2C%20authentic%20cultural%20blend%20natural%20documentary%20style%20photography&width=900&height=500&seq=nri001&orientation=landscape"
                  alt="NRI Family Performing Puja"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="w-full lg:w-[55%]">
              <h2 className="text-3xl sm:text-4xl font-light text-[#2E2E2E] mb-6">
                For Devotees <span className="font-medium">Living Abroad</span>
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6 font-light">
                Living abroad should not mean losing connection with your spiritual roots. EkDharma enables NRIs to perform
                authentic Hindu rituals in India without travel.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#C78A2B]"></i>
                  <span className="font-light">No physical presence required</span>
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#C78A2B]"></i>
                  <span className="font-light">Rituals performed with proper sankalp</span>
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#C78A2B]"></i>
                  <span className="font-light">Photos and videos shared digitally</span>
                </li>
              </ul>
              <Link
                to="/for-nris"
                className="inline-flex items-center gap-2 px-8 py-3 bg-[#C78A2B] text-white rounded-xl text-base font-medium hover:bg-[#B8860B] transition-all whitespace-nowrap"
              >
                Learn More for NRIs
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-light text-[#2E2E2E] mb-4">Why Devotees Trust EkDharma</h2>
            <div className="w-24 h-0.5 bg-[#C78A2B] mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-2xl p-6 text-center hover:shadow-lg transition-all">
              <i className="ri-shield-check-line text-4xl text-[#C78A2B] mb-4 block"></i>
              <h3 className="text-lg font-semibold text-[#2E2E2E] mb-2">Authenticity</h3>
              <p className="text-sm text-gray-600 font-light">No shortcuts, no exaggeration</p>
            </div>

            <div className="bg-white rounded-2xl p-6 text-center hover:shadow-lg transition-all">
              <i className="ri-eye-line text-4xl text-[#C78A2B] mb-4 block"></i>
              <h3 className="text-lg font-semibold text-[#2E2E2E] mb-2">Transparency</h3>
              <p className="text-sm text-gray-600 font-light">Clear process &amp; communication</p>
            </div>

            <div className="bg-white rounded-2xl p-6 text-center hover:shadow-lg transition-all">
              <i className="ri-heart-line text-4xl text-[#C78A2B] mb-4 block"></i>
              <h3 className="text-lg font-semibold text-[#2E2E2E] mb-2">Compassion</h3>
              <p className="text-sm text-gray-600 font-light">No fear-based practices</p>
            </div>

            <div className="bg-white rounded-2xl p-6 text-center hover:shadow-lg transition-all">
              <i className="ri-global-line text-4xl text-[#C78A2B] mb-4 block"></i>
              <h3 className="text-lg font-semibold text-[#2E2E2E] mb-2">Accessibility</h3>
              <p className="text-sm text-gray-600 font-light">Global time-zone friendly</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-light text-[#2E2E2E] mb-4">What Devotees Say</h2>
            <div className="w-24 h-0.5 bg-[#C78A2B] mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-[#FAF7F2] rounded-2xl p-6 shadow-md">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#C78A2B]"></i>
                ))}
              </div>
              <p className="text-sm text-gray-700 leading-relaxed font-light mb-4 italic">
                "EkDharma helped us perform our daughter's Naamkaran while we were in the UK. Everything was authentic and
                transparent."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#C78A2B]/10 flex items-center justify-center">
                  <i className="ri-user-line text-[#C78A2B]"></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-[#2E2E2E]">Anjali Desai</h4>
                  <p className="text-xs text-gray-500">UK</p>
                </div>
              </div>
            </div>

            <div className="bg-[#FAF7F2] rounded-2xl p-6 shadow-md">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#C78A2B]"></i>
                ))}
              </div>
              <p className="text-sm text-gray-700 leading-relaxed font-light mb-4 italic">
                "EkDharma helped us perform our father's Shradh in Gaya while we were in the USA. The process was transparent
                and respectful."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#C78A2B]/10 flex items-center justify-center">
                  <i className="ri-user-line text-[#C78A2B]"></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-[#2E2E2E]">Rajesh Sharma</h4>
                  <p className="text-xs text-gray-500">USA</p>
                </div>
              </div>
            </div>

            <div className="bg-[#FAF7F2] rounded-2xl p-6 shadow-md">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#C78A2B]"></i>
                ))}
              </div>
              <p className="text-sm text-gray-700 leading-relaxed font-light mb-4 italic">
                "We booked a Satyanarayan Katha through EkDharma while living in Singapore. Everything was handled professionally."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#C78A2B]/10 flex items-center justify-center">
                  <i className="ri-user-line text-[#C78A2B]"></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-[#2E2E2E]">Meera Iyer</h4>
                  <p className="text-xs text-gray-500">Singapore</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-[#2E2E2E]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-light text-white mb-6">Begin Your Spiritual Journey</h2>
          <p className="text-lg text-gray-300 mb-8 font-light">
            Reconnect with your faith — with dignity and devotion.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/book-ritual"
              className="px-10 py-4 bg-[#C78A2B] text-white rounded-full text-base font-medium hover:bg-[#B8860B] transition-all whitespace-nowrap"
            >
              Book a Ritual
            </Link>
            <button
              onClick={handleChatWithUs}
              className="px-10 py-4 border-2 border-white text-white rounded-full text-base font-medium hover:bg-white hover:text-[#2E2E2E] transition-all whitespace-nowrap inline-flex items-center gap-2"
            >
              <i className="ri-chat-3-line"></i>
              Chat with Us
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black rounded-t-[60px] pt-16 pb-10 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-16">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="https://public.readdy.ai/ai/img_res/15313995-b9c8-4137-afe3-659f58e4dd9d.png"
                  alt="EkDharma Logo"
                  className="h-10 w-10 object-contain"
                />
                <h3 className="text-3xl font-light text-white">EkDharma</h3>
              </div>
              <p className="text-base text-gray-400 leading-relaxed mb-8 font-light">
                Connecting devotees worldwide with authentic Sanatana Dharma traditions.
              </p>

              <form onSubmit={handleNewsletterSubmit} data-readdy-form id="newsletter-subscription" className="mb-6">
                <input
                  type="email"
                  name="email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  placeholder="Enter your email"
                  disabled={newsletterStatus === 'submitting'}
                  className="w-full bg-transparent border-b border-white text-white placeholder-gray-500 py-3 focus:outline-none focus:border-[#C78A2B] transition-colors disabled:opacity-50 text-sm"
                  required
                />
                {newsletterMessage && (
                  <p className={`text-xs mt-3 ${newsletterStatus === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                    {newsletterMessage}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={newsletterStatus === 'submitting'}
                  className="mt-4 px-6 py-2.5 bg-white text-black rounded-full text-sm font-medium hover:bg-gray-200 transition-all inline-flex items-center gap-2 disabled:opacity-50"
                >
                  {newsletterStatus === 'submitting' ? 'Subscribing...' : 'Subscribe'}
                  <i className="ri-arrow-right-line"></i>
                </button>
              </form>
            </div>

            <div className="lg:col-span-3">
              <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-5">Quick Links</h4>
              <div className="space-y-4">
                <Link to="/services" className="block text-white text-sm hover:text-[#C78A2B] transition-colors">
                  Our Services
                </Link>
                <Link to="/temples" className="block text-white text-sm hover:text-[#C78A2B] transition-colors">
                  Sacred Temples
                </Link>
                <Link to="/priests" className="block text-white text-sm hover:text-[#C78A2B] transition-colors">
                  Our Priests
                </Link>
                <Link to="/for-nris" className="block text-white text-sm hover:text-[#C78A2B] transition-colors">
                  For NRIs
                </Link>
                <Link to="/festivals" className="block text-white text-sm hover:text-[#C78A2B] transition-colors">
                  Festivals
                </Link>
                <Link to="/about" className="block text-white text-sm hover:text-[#C78A2B] transition-colors">
                  About Us
                </Link>
              </div>
            </div>

            <div className="lg:col-span-4">
              <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-4">Connect</h4>
              <p className="text-white text-base mb-2">+91 9XXXXXXXXX</p>
              <p className="text-white text-base mb-6">info@ekdharma.com</p>

              <a
                href="https://wa.me/919XXXXXXXXX?text=Hello%20EkDharma%20Inquiry"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-4 py-2.5 border border-white text-white rounded hover:bg-white hover:text-[#2E2E2E] transition-colors text-sm"
              >
                <i className="ri-whatsapp-line mr-2"></i> WhatsApp Us
              </a>
            </div>
          </div>

          <div className="text-center text-gray-500 text-xs">
            © {new Date().getFullYear()} EkDharma. All rights reserved. ·{' '}
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#C78A2B] transition-colors"
            >
              Web Design
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
